import { serialize } from 'cookie'
import dayjs from 'dayjs'
import { IncomingMessage, ServerResponse } from 'http'
import IPData, { LookupResponse } from 'ipdata'

import * as T from 'types'

export const continentalUnitedStatesBounds = {
  north: 50.382808,
  south: 22.521208,
  east: -64.945392,
  west: -126.736342,
}

interface LatLng {
  latitude: number
  longitude: number
}

// adapted from: https://stackoverflow.com/a/74027784/4445765
export const isPointInBoundaries = (point: LatLng, bottomLeft: LatLng, topRight: LatLng) => {
  const isLongInRange = () => {
    if (topRight.longitude < bottomLeft.longitude) {
      return point.longitude >= bottomLeft.longitude || point.longitude <= topRight.longitude
    }

    return point.longitude >= bottomLeft.longitude && point.longitude <= topRight.longitude
  }

  return (
    point.latitude >= bottomLeft.latitude && point.latitude <= topRight.latitude && isLongInRange()
  )
}

export const getIsPointInContiguousUs = ({ latitude, longitude }: LatLng) => {
  const { north, south, east, west } = continentalUnitedStatesBounds
  const isWithinBounds = isPointInBoundaries(
    { latitude, longitude },
    { latitude: south, longitude: west },
    { latitude: north, longitude: east },
  )
  return isWithinBounds
}

const statesObj: { [key: string]: string } = {
  CA: 'California',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  TX: 'Texas',
  UT: 'Utah',
  WA: 'Washington',
  WI: 'Wisconsin',
}

export const getStateNameFromCode = (code: string) => statesObj[code]

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const testIps = {
  NY: '69.193.168.152',
  NC: '75.190.208.33',
  CA: '107.77.213.142',
  GA: '73.184.32.16',
}

export const getDataFromIP = async (ip = '') => {
  // ip lookup does not work on `localhost`
  // change to true to test with a specific IP on localhost
  // even if true, this value will not affect prod functionality
  const debugMode = true
  const debugIP = testIps.NY
  const lookupIp = process.env.NODE_ENV !== 'production' && debugMode ? debugIP : ip

  const ipData = new IPData(process.env.IP_DATA_KEY || '')

  const locationData = (await ipData.lookup(lookupIp)) || {}
  return locationData
}

export const getLocationDataFromRequest = (req: IncomingMessage | undefined) => {
  if (!req) return Promise.resolve({} as LookupResponse)

  // Explanation - we're behind a proxy and AWS load balancers: https://en.wikipedia.org/wiki/X-Forwarded-For
  const forwarded = req?.headers['x-forwarded-for']
  const headerExists = typeof forwarded === 'string'
  const ip = headerExists ? forwarded.split(/, /)[0] : req?.socket.remoteAddress
  return getDataFromIP(ip)
}

export const setLocationCookiesServerSide = (
  latitude?: number,
  longitude?: number,
  res?: ServerResponse,
) => {
  if (latitude && longitude && res) {
    const expires = dayjs().add(1, 'day').toDate()
    res?.setHeader('Set-Cookie', [
      serialize('latitude', String(latitude), { expires, path: '/' }),
      serialize('longitude', String(longitude), { expires, path: '/' }),
    ])
  }
}

export const getStateFromStateCode = (stateCode: string, states?: T.IState[]) => {
  if (!states?.length) return null

  const foundState = states.find(state => state.abbreviation === stateCode)
  return foundState || null
}
